/**
 * Shared utility functions for the AI search module: id generation, text
 * truncation/markdown rendering, Indian-currency price formatting, HTML
 * sanitization, and small keyboard/debounce helpers used across the chat UI.
 */

let DOMPurify = null;
try {
  DOMPurify = require("dompurify");
} catch { /* not installed — fallback sanitizer below */ }

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["b", "strong", "em", "i", "ul", "ol", "li", "p", "br", "span", "a", "h3", "h4", "code", "pre", "hr", "table", "thead", "tbody", "tr", "th", "td", "div"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
};
if (DOMPurify) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

const SAFE_TAGS = new Set([
  "b", "strong", "em", "i", "ul", "ol", "li", "p", "br", "span", "a",
  "h3", "h4", "code", "pre", "hr", "table", "thead", "tbody", "tr", "th",
  "td", "div"
]);
const SAFE_ATTRS = new Set(["href", "target", "rel", "class"]);

function fallbackSanitize(html) {
  if (!html) return "";
  let cleaned = html.replace(/<(script|iframe|object|embed|style|form|input|textarea|button|select)\b[^>]*>[\s\S]*?<\/\1>/gi, "");
  cleaned = cleaned.replace(/<(script|iframe|object|embed|style|form|input|textarea|button|select)\b[^>]*\/?>/gi, "");
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, "");
  cleaned = cleaned.replace(/href\s*=\s*["']?\s*javascript:/gi, 'href="');
  cleaned = cleaned.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (match, tagName) => {
    const tag = tagName.toLowerCase();
    if (SAFE_TAGS.has(tag)) {
      if (match.startsWith("</")) return match;
      return match.replace(/\s+([a-zA-Z-]+)\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/g, (attrMatch, attrName) => {
        return SAFE_ATTRS.has(attrName.toLowerCase()) ? attrMatch : "";
      });
    }
    return "";
  });
  return cleaned;
}

export function sanitizeHtml(raw) {
  if (DOMPurify) return DOMPurify.sanitize(raw, PURIFY_CONFIG);
  return fallbackSanitize(raw);
}

export const uid = () => crypto.randomUUID();

export const truncateWords = (text, count = 5) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= count) return text;
  return words.slice(0, count).join(" ") + "…";
};

// True when a chat's title was generated (default, first-message, or a
// search-derived chatTitle) rather than typed by the user via rename.
// A bare reply ("Yes" to the Talk to Me gate) says nothing about the search, so it doesn't name the chat.
export const PLACEHOLDER_TITLE = "New home search";
const TRIVIAL_REPLY = /^(yes|yeah|yep|sure|ok|okay|no|nope|nah)[.!]*$/i;
export const isTrivialTitle = (title) => TRIVIAL_REPLY.test((title || "").trim());
export const titleForFirstMessage = (text) =>
  TRIVIAL_REPLY.test((text || "").trim()) ? PLACEHOLDER_TITLE : truncateWords(text);

export const isAutoTitle = (title, messages = []) => {
  if (!title || title === "New Chat" || title === "AI Search Chat" || title === PLACEHOLDER_TITLE) return true;
  if (TRIVIAL_REPLY.test(title.trim())) return true;
  if (messages.some((m) => m.role === "ai" && m.chatTitle === title)) return true;
  const firstUser = messages.find((m) => m.role === "user")?.text || "";
  return !!firstUser && (title === truncateWords(firstUser) || title === firstUser.slice(0, 40));
};

// Appends the requirements the search could not check (backend requirementDisclosures) to a reply.
// Appends what priority matching left out or couldn't check (backend excludedByFixed /
// requirementDisclosures), worded as in the business guide: "1 listing was left out because it
// doesn't state amenities. I can't check "peaceful neighbourhood" yet, so it hasn't affected the order."
const OWN_MESSAGE = new Set(["CONFLICTS_FIXED", "DUPLICATE_KEY", "NO_VALUE", "METRIC_ORDER_NEEDED", "PROVIDER_UNAVAILABLE"]);

export const withDisclosures = (text, disclosures, excludedByFixed = [], eligibleCount = null) => {
  const notes = [];
  if (eligibleCount > 0) {
    notes.push(eligibleCount === 1
        ? "Showing 1 property that meets all your must-haves."
        : `Showing ${eligibleCount} properties that meet all your must-haves.`);
  }
  for (const e of Array.isArray(excludedByFixed) ? excludedByFixed : []) {
    if (e.reasonCode !== "NOT_STATED" || !e.count) continue;
    const what = (e.requirement || "").split(":")[0].trim().toLowerCase() || "a must-have";
    notes.push(e.count === 1
        ? `1 listing was left out because it doesn't state ${what}.`
        : `${e.count} listings were left out because they don't state ${what}.`);
  }
  const list = Array.isArray(disclosures) ? disclosures : [];
  for (const d of list.filter((d) => OWN_MESSAGE.has(d.reasonCode))) notes.push(d.message);
  const cantCheck = list.filter((d) => !OWN_MESSAGE.has(d.reasonCode));
  if (cantCheck.length > 0) {
    const names = cantCheck.map((d) => `"${d.text}"`).join(", ");
    notes.push(`I can't check ${names} yet, so ${cantCheck.length === 1 ? "it hasn't" : "they haven't"} affected the order.`);
  }
  return notes.length ? `${text}\n\n${notes.join(" ")}` : text;
};

export const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

// Indian numbering: amounts >= 1 crore (1e7) show as "X.XX Cr", >= 1 lakh
// (1e5) show as "X.XX L" — the conventional format for Indian real estate.
export const formatPrice = (price) => {
  if (!price) return "Price on request";

  const s = String(price);
  if (s.includes("₹") || /\bCr\b|\bL\b/i.test(s)) return s;

  const cleaned = s.replace(/[₹,]/g, "");
  const num = Number(cleaned);

  if (isNaN(num)) return price;

  if (num >= 10000000) {
    return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  }

  if (num >= 100000) {
    return `₹ ${(num / 100000).toFixed(2)} L`;
  }

  return `₹ ${num.toLocaleString("en-IN")}`;
};

export const debounce = (fn, ms = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

export const parseMarkdown = (text) => {
  if (!text) return "";

  const codeBlocks = [];
  text = text.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(code.trim());
    return `%%CODE_BLOCK_${idx}%%`;
  });

  const inlineCodes = [];
  text = text.replace(/`([^`]+)`/g, (_, code) => {
    const idx = inlineCodes.length;
    inlineCodes.push(code);
    return `%%INLINE_CODE_${idx}%%`;
  });

  text = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  text = text.replace(/^####\s+(.+)$/gm, '<h4 class="ai-h4">$1</h4>');
  text = text.replace(/^###\s+(.+)$/gm,  '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^##\s+(.+)$/gm,   '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^#\s+(.+)$/gm,    '<h3 class="ai-h3">$1</h3>');

  text = text.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  text = text.replace(/\*\*(.+?)\*\*/g,       "<strong>$1</strong>");
  text = text.replace(/\*([^*\n]+?)\*/g,        "<em>$1</em>");

  text = text.replace(/^[-*]\s+(.+)$/gm, "%%BULLET_LI%%$1%%END_LI%%");

  text = text.replace(/^\d+\.\s+(.+)$/gm, "%%NUMBER_LI%%$1%%END_LI%%");

  text = text.replace(/(%%BULLET_LI%%[\s\S]*?%%END_LI%%(?:\n%%BULLET_LI%%[\s\S]*?%%END_LI%%)*)/g, (block) => {
    const items = block.replace(/%%BULLET_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");
    return '<ul class="ai-list">' + items.replace(/\n/g, "") + "</ul>";
  });

  text = text.replace(/(%%NUMBER_LI%%[\s\S]*?%%END_LI%%(?:\n%%NUMBER_LI%%[\s\S]*?%%END_LI%%)*)/g, (block) => {
    const items = block.replace(/%%NUMBER_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");
    return '<ol class="ai-list ai-ol">' + items.replace(/\n/g, "") + "</ol>";
  });

  text = text.replace(/%%BULLET_LI%%/g, "<li>").replace(/%%NUMBER_LI%%/g, "<li>").replace(/%%END_LI%%/g, "</li>");

  text = text.replace(/^---$/gm, "<hr/>");

  const tableRegex = /^(\|.+\|[ \t]*\n)(\|[-: |]+\|[ \t]*\n)((\|.+\|[ \t]*\n?)*)/gm;
  text = text.replace(tableRegex, (match, header, separator, body) => {
    const parseRow = (row) => row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
    const headers = parseRow(header);
    const rows    = body.trim().split("\n").filter(r => r.trim().startsWith("|"));
    const thead   = "<thead><tr>" + headers.map(h => `<th>${h}</th>`).join("") + "</tr></thead>";
    const tbody   = "<tbody>" + rows.map(r => {
      const cells = parseRow(r);
      return "<tr>" + cells.map(c => `<td>${c}</td>`).join("") + "</tr>";
    }).join("") + "</tbody>";
    return `<div class="ai-table-wrap"><table class="ai-table">${thead}${tbody}</table></div>\n`;
  });

  text = text.replace(/\n{4,}/g, "\n\n");
  text = text.replace(/\n{2,}/g, "<br/><br/>");
  text = text.replace(/\n/g, "<br/>");

  text = text.replace(/%%CODE_BLOCK_(\d+)%%/g, (_, i) =>
      '<pre class="code-block"><code>' + codeBlocks[parseInt(i)] + '</code></pre>'
  );
  text = text.replace(/%%INLINE_CODE_(\d+)%%/g, (_, i) =>
      '<code class="inline-code">' + inlineCodes[parseInt(i)] + '</code>'
  );

  return text;
};


export const isShortcut = (e, key, mod = "ctrl") => {
  const modKey = mod === "ctrl" ? e.ctrlKey || e.metaKey : e[`${mod}Key`];
  return modKey && e.key.toLowerCase() === key.toLowerCase();
};

// Shared authed-JSON-fetch wrapper -- used by PersonalAgentPage/GuidedSearch
// and any other AI-module caller that needs a bearer-token POST/GET with
// consistent error surfacing (raises with the backend's `detail` message
// when present, so callers can show it directly).
export async function authedFetch(url, token, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `HTTP ${response.status}`);
  }
  return response.status === 204 ? null : response.json();
}
